import React, { useState, useRef } from "react";
import {
  forgotPasswordUser,
  forgotPasswordverifyOtp,
  changePassword, // You'll create this service
} from "../services/auth";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import toast from 'react-hot-toast';

const ForgotAndOtp: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<"email" | "otp" | "reset">("email");
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState<string | null>(null);

  const [errors, setErrors] = useState<{ email?: string }>({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  // OTP
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ===== Email Step =====
  const validateEmail = () => {
    const newErrors: { email?: string } = {};
    if (!email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = "Email is invalid";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail()) return;
    setLoading(true);
    setApiError("");
    try {
      const response: any = await forgotPasswordUser({ email });
      if (response.data.success) {
        const id = response?.data?.data?._id;
        if (id) {
          setUserId(id);
          setStep("otp");
        } else {
          throw new Error("User ID not found.");
        }
      } else {
        setApiError(response?.data?.message || "No Email Found");
      }
    } catch (error: any) {
      setApiError(error.response?.data?.message || "Forgot Password Failed");
    } finally {
      setLoading(false);
    }
  };

  // ===== OTP Step =====
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < 3) inputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length !== 4) {
      setOtpError("Please enter a valid 4-digit OTP.");
      return;
    }
    setOtpError("");
    setLoading(true);
    try {
      await forgotPasswordverifyOtp({ id: userId,email: email, otp: code });
      setStep("reset");
    } catch (err: any) {
      setOtpError(err.response?.data?.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  // ===== Reset Password Step =====
  const validatePasswords = () => {
    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return false;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePasswords() || !userId) return;
    setLoading(true);
    try {
      const response: any = await changePassword({ id: userId,email:email, password: newPassword });
      if(response.data.success){
        toast.success('Password changed successfully!');
        navigate("/login");
      } else {
        setPasswordError(response?.data?.message || "Password change failed");
      }
     
    } catch (err: any) {
      setPasswordError(err.response?.data?.message || "Password change failed");
    } finally {
      setLoading(false);
    }
  };

  // ===== UI Layout =====
  const renderForm = () => {
    switch (step) {
      case "email":
        return (
          <>
            <h2 className="text-3xl font-semibold text-gray-800 mb-6">
              Forgot Password
            </h2>
            <form onSubmit={handleEmailSubmit} className="space-y-5">
              {apiError && (
                <div className="bg-red-100 text-red-700 p-3 rounded">
                  {apiError}
                </div>
              )}
              <div>
                <label className="block mb-1 text-gray-600">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-4 py-2 border ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400`}
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm">{errors.email}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600"
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
              <div className="text-center mt-3">
              <p className="text-sm">
                Already have an account?{' '}
                <a href="/login" className="text-green-600 hover:underline">
                  Login
                </a>
              </p>
            </div>
            </form>
          </>
        );

      case "otp":
        return (
          <>
            <h2 className="text-3xl font-semibold text-gray-800 mb-6">
              OTP Verification
            </h2>
            <form onSubmit={handleOtpSubmit} className="space-y-6">
              {otpError && (
                <div className="bg-red-100 text-red-700 p-3 rounded">
                  {otpError}
                </div>
              )}
              <div className="flex justify-between space-x-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      if (el) inputRefs.current[index] = el;
                    }}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    className="w-14 h-14 text-center text-xl border border-gray-300 rounded-lg focus:ring-green-400"
                  />
                ))}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600"
              >
                {loading ? "Verifying..." : "Verify OTP"}
              </button>
                <div className="text-center mt-3">
                  <p className="text-sm">
                    Already have an account?{' '}
                    <a href="/login" className="text-green-600 hover:underline">
                      Login
                    </a>
                  </p>
              </div>
            </form>
          </>
        );

        case 'reset':
          return (
            <>
              <h2 className="text-3xl font-semibold text-gray-800 mb-6">Change Password</h2>
              <form onSubmit={handlePasswordSubmit} className="space-y-5">
                {passwordError && (
                  <div className="bg-red-100 text-red-700 p-3 rounded">{passwordError}</div>
                )}
        
                {/* New Password */}
                <div className="relative">
                  <label className="block mb-1 text-gray-600">New Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`w-full px-4 py-2 border ${
                      passwordError ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400`}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    className="absolute top-10 right-3 text-gray-500"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
        
                {/* Confirm Password */}
                <div className="relative">
                  <label className="block mb-1 text-gray-600">Confirm Password</label>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-4 py-2 border ${
                      passwordError ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400`}
                    placeholder="Confirm password"
                  />
                  <button
                    type="button"
                    className="absolute top-10 right-3 text-gray-500"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
        
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600"
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
                 {/* Login Link */}
            <div className="text-center mt-3">
              <p className="text-sm">
                Already have an account?{' '}
                <a href="/login" className="text-green-600 hover:underline">
                  Login
                </a>
              </p>
            </div>
              </form>

            </>
          );
        
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white rounded-2xl shadow-lg flex max-w-4xl w-full">
        {/* Left Panel */}
        <div className="w-1/2 bg-gradient-to-br from-green-400 to-green-600 text-white flex flex-col items-center justify-center rounded-l-2xl p-8">
          <img
            src={require("../assets/logo.png")}
            alt="Logo"
            className="w-40 h-40 object-contain mb-4"
          />
          <h2 className="text-2xl font-bold">
            {step === "email"
              ? "Welcome Back!"
              : step === "otp"
              ? "Verify OTP"
              : "Set New Password"}
          </h2>
          <p className="text-center mt-2">
            {step === "email"
              ? "Enter your email to reset your password"
              : step === "otp"
              ? "Enter the OTP sent to your email"
              : "Create a secure new password"}
          </p>
        </div>

        {/* Right Panel */}
        <div className="w-1/2 p-8">{renderForm()}</div>
      </div>
    </div>
  );
};

export default ForgotAndOtp;
